import { MorphineRecommendation } from './morphine-algorithm';
import { GISymptoms } from './types/gi-symptoms';

export enum ClonidineRecommendation {
  StartQ3,
  StartQ6,
  Increase,
  Decrease,
  Discontinue,
  Discharge,
  NoChange,
  NoStarted,
}

export type InuteroOpiate = {
  isTakingBuprenorphine: boolean;
  isTakingMethadone: boolean;
  isTakingOther: boolean;
};

export type GetClonidineRecommendationRequest = {
  clonidineDose: number;
  clonidineFrequency: number;
  morphineRecommendationResult: { recommendationCoded: MorphineRecommendation; forceClonidineStart: boolean };
  morphineDose: number;
  priorAssessmentGISymptoms: GISymptoms;
  currentAssessmentGISymptoms: GISymptoms;
  hasEverTakenClonidine: boolean;
  hasEverTakenMorphine: boolean;
  inuteroOpiates: InuteroOpiate;
  hoursSinceBirth: number;
  hoursSinceLastClonidineChange: number | null; // Hours since last change in clonidine dose
  hoursSinceLastMorphineChange: number | null; // Hours since last change in morphine dose
  isIncreaseComparedToSixHoursAgo: boolean; // Has the dose increased compared to six hours ago
  isLastAssessmentRecommendationAnIncrease: boolean; // Is the last assessment recommendation an increase
  hasTrialedOffClonidine: boolean; // Has the patient trialed off clonidine
};

type GetClonidineRecommendationRequestComputed = GetClonidineRecommendationRequest & {
  isTakingClonidine: boolean;
  isTakingMorphine: boolean;
  hasConsistentPoorGI: boolean;
};

export type ClonidineRecommendationResult = {
  calculatedDose: number;
  frequency: number;
  recommendation: string;
  recommendationCoded: ClonidineRecommendation;
  note: string;
  isRestart: boolean;
};

const checkStartOrIncrease = (request: GetClonidineRecommendationRequestComputed): ClonidineRecommendationResult => {
  if (request.isTakingClonidine) return checkTakingClonidinePath(request);
  return checkNotTakingClonidinePath(request);
};

const MORPHINE_ALGO_RESULTS_AFFECTING_CLONIDINE = [MorphineRecommendation.Start, MorphineRecommendation.Increase];
const WAIT_HOURS_BEFORE_DISCHARGE = 24;
const WAIT_HOURS_BEFORE_DISCONTINUE = 18;
const WAIT_HOURS_BEFORE_DECREASE_DISCONTINUE_DISCHARGE = 6;
const WAIT_HOURS_BEFORE_INCREASE = 6;
const MORPHINE_DOSE_MAX_FOR_CLONIDINE_DISCONTINUE = 0.02;
const CLONIDINE_MAX_DOSE = 2.0;
const WAIT_HOURS_OBSERVATION_BEFORE_DISCHARGE_METHADONE = 120; // 5 days
const WAIT_HOURS_OBSERVATION_BEFORE_DISCHARGE_BUPRENORPHINE = 96; // 4 days
const WAIT_HOURS_OBSERVATION_BEFORE_DISCHARGE_OTHER = 72; // 3 days

const checkMorphineAlgoAffectingClonidine = (
  forceClonidine: boolean,
  morphineResult: MorphineRecommendation,
): boolean => forceClonidine || MORPHINE_ALGO_RESULTS_AFFECTING_CLONIDINE.includes(morphineResult);

export const checkForConsistentPoorGI = (priorGISymptoms: GISymptoms, currentGISymptoms: GISymptoms): boolean => {
  return (
    (priorGISymptoms.poorFeeding && currentGISymptoms.poorFeeding) ||
    (priorGISymptoms.poorStooling && currentGISymptoms.poorStooling)
  );
};

const checkTakingClonidinePath = (
  request: GetClonidineRecommendationRequestComputed,
): ClonidineRecommendationResult => {
  // Increase
  if (
    checkMorphineAlgoAffectingClonidine(
      request.morphineRecommendationResult.forceClonidineStart,
      request.morphineRecommendationResult.recommendationCoded,
    )
  )
    return checkIncrease(
      request.isIncreaseComparedToSixHoursAgo,
      request.isLastAssessmentRecommendationAnIncrease,
      request.hoursSinceLastClonidineChange,
      request.hasEverTakenClonidine,
      request.clonidineDose,
      request.clonidineFrequency,
      `Taking Clonidine -> Morphine algorithm affecting Clonidine (forceClonidine: ${
        request.morphineRecommendationResult.forceClonidineStart
      }, morphineRecommendation: ${MorphineRecommendation[request.morphineRecommendationResult.recommendationCoded]})`,
    );
  if (request.hasConsistentPoorGI)
    return checkIncrease(
      request.isIncreaseComparedToSixHoursAgo,
      request.isLastAssessmentRecommendationAnIncrease,
      request.hoursSinceLastClonidineChange,
      request.hasEverTakenClonidine,
      request.clonidineDose,
      request.clonidineFrequency,
      `Taking Clonidine -> Consistent poor GI symptoms detected (prior: ${JSON.stringify(
        request.priorAssessmentGISymptoms,
      )}, current: ${JSON.stringify(request.currentAssessmentGISymptoms)})`,
    );
  // Decrease/Discontinue/Discharge
  return checkDecreaseDiscontinueDischarge(
    request,
    'Taking Clonidine -> No increase criteria met, checking decrease/discontinue/discharge',
  );
};

const checkDecreaseDiscontinueDischarge = (
  request: GetClonidineRecommendationRequestComputed,
  pathNote: string,
): ClonidineRecommendationResult => {
  // Wait longer
  if (
    request.hoursSinceLastClonidineChange !== null &&
    request.hoursSinceLastClonidineChange < WAIT_HOURS_BEFORE_DECREASE_DISCONTINUE_DISCHARGE
  )
    return handleNoChange(
      `${pathNote} -> Too soon for decrease/discontinue/discharge. Waiting ${WAIT_HOURS_BEFORE_DECREASE_DISCONTINUE_DISCHARGE} hours since last change (current: ${request.hoursSinceLastClonidineChange}h)`,
      request.clonidineDose,
      request.clonidineFrequency,
    );

  // Discharge
  if (request.clonidineDose === 1.0 && request.clonidineFrequency === 6)
    return checkDischarge(
      request.isTakingMorphine,
      request.clonidineDose,
      request.clonidineFrequency,
      request.hoursSinceLastClonidineChange,
      request.hoursSinceLastMorphineChange,
      request.hasEverTakenClonidine,
      request.hasEverTakenMorphine,
      `${pathNote} -> Current dose 1.0mcg/kg q6, checking discharge criteria`,
    );
  // Decrease
  if (request.clonidineDose > 1.0 && request.clonidineFrequency === 3)
    return handleDecrease(
      `${pathNote} -> Current dose ${request.clonidineDose}mcg/kg q3 > 1.0, decreasing by 0.5mcg/kg`,
      request.clonidineDose,
    );
  // Discontinue
  if (request.clonidineDose <= 1.0 && request.clonidineFrequency === 3)
    if (request.hasTrialedOffClonidine)
      return handleStart(
        `${pathNote} -> Current dose ${request.clonidineDose}mcg/kg q3 <= 1.0 and has trialed off Clonidine, decreasing to 1.0mcg/kg q6`,
        request.hasEverTakenClonidine,
        6,
      );
    else
      return checkDiscontinue(
        request.hoursSinceLastClonidineChange,
        request.morphineDose,
        request.clonidineDose,
        request.clonidineFrequency,
        `${pathNote} -> Current dose ${request.clonidineDose}mcg/kg q3 <= 1.0, checking discontinue criteria`,
      );

  return handleNoChange(
    `${pathNote} -> No specific criteria met for decrease/discontinue/discharge (dose: ${request.clonidineDose}mcg/kg q${request.clonidineFrequency})`,
    request.clonidineDose,
    request.clonidineFrequency,
  );
};

const checkDiscontinue = (
  hoursSinceLastClonidineChange: number | null,
  morphineDose: number,
  clonidineDose: number,
  clonidineFrequency: number,
  pathNote: string,
): ClonidineRecommendationResult => {
  if (hoursSinceLastClonidineChange !== null && hoursSinceLastClonidineChange < WAIT_HOURS_BEFORE_DISCONTINUE)
    return handleNoChange(
      `${pathNote} -> Waiting ${WAIT_HOURS_BEFORE_DISCONTINUE} hours before discontinue. Hours since last change: ${hoursSinceLastClonidineChange}`,
      clonidineDose,
      clonidineFrequency,
    );
  if (morphineDose > MORPHINE_DOSE_MAX_FOR_CLONIDINE_DISCONTINUE)
    return handleNoChange(
      `${pathNote} -> Waiting for Morphine dose to drop below ${MORPHINE_DOSE_MAX_FOR_CLONIDINE_DISCONTINUE} (current: ${morphineDose})`,
      clonidineDose,
      clonidineFrequency,
    );
  return handleDiscontinue(
    `${pathNote} -> All discontinue criteria met (morphine dose: ${morphineDose}, hours since change: ${hoursSinceLastClonidineChange})`,
  );
};

const checkIncrease = (
  isIncreaseComparedToSixHoursAgo: boolean,
  isLastAssessmentRecommendationAnIncrease: boolean,
  hoursSinceLastClonidineChange: number | null,
  hasEverTakenClonidine: boolean,
  clonidineDose: number,
  clonidineFrequency: number,
  pathNote: string,
): ClonidineRecommendationResult => {
  if (hoursSinceLastClonidineChange !== null && hoursSinceLastClonidineChange < WAIT_HOURS_BEFORE_INCREASE)
    return handleNoChange(
      `${pathNote} -> Waiting ${WAIT_HOURS_BEFORE_INCREASE} hours since last Clonidine change (current: ${hoursSinceLastClonidineChange}h)`,
      clonidineDose,
      clonidineFrequency,
    );
  if (isIncreaseComparedToSixHoursAgo)
    return handleNoChange(
      `${pathNote} -> Too soon to increase due to recent increase compared to 6 hours ago`,
      clonidineDose,
      clonidineFrequency,
    );
  if (isLastAssessmentRecommendationAnIncrease)
    return handleNoChange(
      `${pathNote} -> Too soon to increase due to last assessment recommendation being an increase`,
      clonidineDose,
      clonidineFrequency,
    );
  if (clonidineDose === 1.0 && clonidineFrequency === 6)
    return handleStart(
      `${pathNote} -> Current dose 1.0mcg/kg q6, increasing to 1.0mcg/kg q3`,
      hasEverTakenClonidine,
      3,
    );
  if (clonidineDose >= CLONIDINE_MAX_DOSE)
    return handleNoChange(
      `${pathNote} -> Maximum dose reached (${clonidineDose} >= ${CLONIDINE_MAX_DOSE})`,
      clonidineDose,
      clonidineFrequency,
    );
  return handleIncrease(
    `${pathNote} -> All increase criteria met, increasing from ${clonidineDose}mcg/kg q${clonidineFrequency}`,
    clonidineDose,
  );
};

const checkNotTakingClonidinePath = (
  request: GetClonidineRecommendationRequestComputed,
): ClonidineRecommendationResult => {
  // Checking for Start
  const checkStartResult = checkStart(
    request.hasEverTakenClonidine,
    request.morphineRecommendationResult.recommendationCoded,
    request.morphineRecommendationResult.forceClonidineStart,
    'Not Taking Clonidine',
  );
  if (checkStartResult) return checkStartResult;

  // Check Restart
  if (request.hasEverTakenClonidine) {
    const checkRestartResult = checkRestart(
      request.hasEverTakenClonidine,
      request.hasConsistentPoorGI,
      'Not Taking Clonidine',
    );
    if (checkRestartResult) return checkRestartResult;
    // Restart not needed, check Discharge
    return checkDischarge(
      request.isTakingMorphine,
      request.clonidineDose,
      request.clonidineFrequency,
      request.hoursSinceLastClonidineChange,
      request.hoursSinceLastMorphineChange,
      request.hasEverTakenClonidine,
      request.hasEverTakenMorphine,
      'Not Taking Clonidine -> Has taken Clonidine before but no restart criteria met, checking discharge',
    );
  }

  // Check Discharge
  if (request.hasEverTakenMorphine)
    return checkDischarge(
      request.isTakingMorphine,
      request.clonidineDose,
      request.clonidineFrequency,
      request.hoursSinceLastClonidineChange,
      request.hoursSinceLastMorphineChange,
      request.hasEverTakenClonidine,
      request.hasEverTakenMorphine,
      'Not Taking Clonidine -> Has taken Morphine before, checking discharge',
    );

  // Check Observation Discharge
  return checkObservationDischarge(
    request.inuteroOpiates,
    request.hoursSinceBirth,
    request.clonidineDose,
    request.clonidineFrequency,
    'Not Taking Clonidine -> No prior Clonidine or Morphine, checking observation discharge',
  );
};

const checkStart = (
  hasEverTakenClonidine: boolean,
  morphineRecommendationResult: MorphineRecommendation,
  forceClonidineStart: boolean,
  pathNote: string,
): ClonidineRecommendationResult | null => {
  if (checkMorphineAlgoAffectingClonidine(forceClonidineStart, morphineRecommendationResult))
    return handleStart(
      `${pathNote} -> Start due to Morphine recommendation '${MorphineRecommendation[morphineRecommendationResult]}' (forceClonidine: ${forceClonidineStart})`,
      hasEverTakenClonidine,
      3,
    );
  if (forceClonidineStart) return handleStart(`${pathNote} -> Force start requested`, hasEverTakenClonidine, 3);
  return null; // No start needed
};

const checkRestart = (
  hasEverTakenClonidine: boolean,
  hasConsistentPoorGI: boolean,
  pathNote: string,
): ClonidineRecommendationResult | null => {
  if (hasConsistentPoorGI)
    return handleStart(
      `${pathNote} -> Restarting Clonidine due to consistent poor GI symptoms`,
      hasEverTakenClonidine,
      3,
    );
  return null; // No restart needed
};

const checkDischarge = (
  isTakingMorphine: boolean,
  clonidineDose: number,
  clonidineFrequency: number,
  hoursSinceLastClonidineChange: number | null,
  hoursSinceLastMorphineChange: number | null,
  hasEverTakenClonidine: boolean,
  hasEverTakenMorphine: boolean,
  pathNote: string,
): ClonidineRecommendationResult => {
  if (!isTakingMorphine) {
    if (
      (!hasEverTakenClonidine ||
        hoursSinceLastClonidineChange === null ||
        hoursSinceLastClonidineChange >= WAIT_HOURS_BEFORE_DISCHARGE) &&
      (!hasEverTakenMorphine ||
        hoursSinceLastMorphineChange === null ||
        hoursSinceLastMorphineChange >= WAIT_HOURS_BEFORE_DISCHARGE)
    ) {
      return handleDischarge(
        `${pathNote} -> Not taking Morphine and waited ${WAIT_HOURS_BEFORE_DISCHARGE} hours since last changes (Clonidine: ${
          hasEverTakenClonidine
            ? hoursSinceLastClonidineChange !== null
              ? hoursSinceLastClonidineChange + 'h'
              : 'N/A'
            : 'Never Taken'
        }, Morphine: ${
          hasEverTakenMorphine
            ? hoursSinceLastMorphineChange !== null
              ? hoursSinceLastMorphineChange + 'h'
              : 'N/A'
            : 'Never Taken'
        })`,
      );
    } else {
      return handleNoChange(
        `${pathNote} -> Not taking Morphine but waiting ${WAIT_HOURS_BEFORE_DISCHARGE} hours since last changes (Clonidine: ${
          hasEverTakenClonidine
            ? hoursSinceLastClonidineChange !== null
              ? hoursSinceLastClonidineChange + 'h'
              : 'N/A'
            : 'Never Taken'
        }, Morphine: ${
          hasEverTakenMorphine
            ? hoursSinceLastMorphineChange !== null
              ? hoursSinceLastMorphineChange + 'h'
              : 'N/A'
            : 'Never Taken'
        })`,
        clonidineDose,
        clonidineFrequency,
      );
    }
  }
  return handleNoChange(
    `${pathNote} -> Still taking Morphine (dose: ${clonidineDose}mcg/kg q${clonidineFrequency}), waiting for Morphine to discontinue`,
    clonidineDose,
    clonidineFrequency,
  );
};

const checkObservationDischarge = (
  inuteroOpiates: InuteroOpiate,
  hoursSinceBirth: number,
  clonidineDose: number,
  clonidineFrequency: number,
  pathNote: string,
): ClonidineRecommendationResult => {
  if (inuteroOpiates.isTakingMethadone)
    return checkObservationDischargeOrWait(
      hoursSinceBirth,
      WAIT_HOURS_OBSERVATION_BEFORE_DISCHARGE_METHADONE,
      'Methadone',
      clonidineDose,
      clonidineFrequency,
      `${pathNote} -> In utero exposure to Methadone`,
    );
  else if (inuteroOpiates.isTakingBuprenorphine)
    return checkObservationDischargeOrWait(
      hoursSinceBirth,
      WAIT_HOURS_OBSERVATION_BEFORE_DISCHARGE_BUPRENORPHINE,
      'Buprenorphine',
      clonidineDose,
      clonidineFrequency,
      `${pathNote} -> In utero exposure to Buprenorphine`,
    );
  else
    return checkObservationDischargeOrWait(
      hoursSinceBirth,
      WAIT_HOURS_OBSERVATION_BEFORE_DISCHARGE_OTHER,
      'Other',
      clonidineDose,
      clonidineFrequency,
      `${pathNote} -> In utero exposure to Other opiates`,
    );
};

const checkObservationDischargeOrWait = (
  hoursSinceBirth: number,
  waitInHours: number,
  inuteroOpiateDrugName: string,
  clonidineDose: number,
  clonidineFrequency: number,
  pathNote: string,
): ClonidineRecommendationResult => {
  if (hoursSinceBirth >= waitInHours)
    return handleDischarge(
      `${pathNote} -> Discharging after waiting ${waitInHours} hours after birth (current: ${hoursSinceBirth}h)`,
    );
  else
    return handleNoChange(
      `${pathNote} -> Waiting for ${waitInHours} hours after birth (current: ${hoursSinceBirth}h)`,
      clonidineDose,
      clonidineFrequency,
    );
};

const handleIncrease = (note: string, clonidineDose: number): ClonidineRecommendationResult => {
  return {
    calculatedDose: clonidineDose + 0.5,
    frequency: 3,
    recommendation: '+0.5mcg/kg q3',
    recommendationCoded: ClonidineRecommendation.Increase,
    isRestart: false,
    note,
  };
};

const handleDiscontinue = (note: string): ClonidineRecommendationResult => {
  return {
    calculatedDose: 0,
    frequency: 0,
    recommendation: 'Discontinue',
    recommendationCoded: ClonidineRecommendation.Discontinue,
    isRestart: false,
    note,
  };
};

const handleDecrease = (note: string, clonidineDose: number): ClonidineRecommendationResult => {
  return {
    calculatedDose: clonidineDose - 0.5,
    frequency: 3,
    recommendation: '-0.5mcg/kg q3',
    recommendationCoded: ClonidineRecommendation.Decrease,
    isRestart: false,
    note,
  };
};

const handleDischarge = (note: string): ClonidineRecommendationResult => {
  return {
    calculatedDose: 0.0,
    frequency: 0,
    recommendation: 'Consider Discharge',
    recommendationCoded: ClonidineRecommendation.Discharge,
    isRestart: false,
    note,
  };
};

const handleNoChange = (
  note: string,
  clonidineDose: number,
  clonidineFrequency: number,
): ClonidineRecommendationResult => {
  // If patient is not taking clonidine (dose = 0), return NoStarted instead of NoChange
  if (clonidineDose === 0) {
    return {
      calculatedDose: 0,
      frequency: 0,
      recommendation: 'No Medication',
      recommendationCoded: ClonidineRecommendation.NoStarted,
      isRestart: false,
      note,
    };
  }
  
  // Otherwise return NoChange for patients actively on medication
  return {
    calculatedDose: clonidineDose,
    frequency: clonidineFrequency,
    recommendation: 'No Change',
    recommendationCoded: ClonidineRecommendation.NoChange,
    isRestart: false,
    note,
  };
};

const handleStart = (
  note: string,
  hasEverTakenClonidine: boolean,
  frequency: number,
): ClonidineRecommendationResult => {
  return {
    calculatedDose: 1.0,
    frequency,
    recommendation: `Start 1.0mcg/kg q${frequency}`,
    recommendationCoded: frequency === 6 ? ClonidineRecommendation.StartQ6 : ClonidineRecommendation.StartQ3,
    isRestart: hasEverTakenClonidine,
    note,
  };
};

const fixFloatPrecision = (value: number): number => {
  return parseFloat(value.toPrecision(12));
};

export const getClonidineRecommendation = (
  request: GetClonidineRecommendationRequest,
): ClonidineRecommendationResult => {
  // Compute request
  const computedRequest = request as GetClonidineRecommendationRequestComputed;
  computedRequest.clonidineDose = fixFloatPrecision(request.clonidineDose);
  computedRequest.morphineDose = fixFloatPrecision(request.morphineDose);
  computedRequest.isTakingClonidine = request.clonidineDose > 0;
  if (computedRequest.isTakingClonidine) computedRequest.hasEverTakenClonidine = true;
  computedRequest.isTakingMorphine = request.morphineDose > 0;
  if (computedRequest.isTakingMorphine) computedRequest.hasEverTakenMorphine = true;
  computedRequest.hasConsistentPoorGI = checkForConsistentPoorGI(
    request.priorAssessmentGISymptoms,
    request.currentAssessmentGISymptoms,
  );

  return checkStartOrIncrease(computedRequest);
};
